<div class="results-step">
  <!-- Summary cards -->
  <div class="summary-cards">
    <div class="summary-card success">
      <div class="card-icon">
        <mat-icon>check_circle</mat-icon>
      </div>
      <div class="card-content">
        <div class="card-value">{{ successfulPayments.length }}</div>
        <div class="card-label">Successful Payments</div>
      </div>
    </div>

    <div class="summary-card error">
      <div class="card-icon">
        <mat-icon>error</mat-icon>
      </div>
      <div class="card-content">
        <div class="card-value">{{ failedPayments.length }}</div>
        <div class="card-label">Failed Payments</div>
      </div>
    </div>

    <div class="summary-card warning">
      <div class="card-icon">
        <mat-icon>warning</mat-icon>
      </div>
      <div class="card-content">
        <div class="card-value">{{ skippedPayments.length }}</div>
        <div class="card-label">Skipped</div>
      </div>
    </div>

    <div class="summary-card primary">
      <div class="card-icon">
        <mat-icon>attach_money</mat-icon>
      </div>
      <div class="card-content">
        <div class="card-value">{{ totalAmount | currency }}</div>
        <div class="card-label">Total Processed</div>
      </div>
    </div>

    @if (hasCostOfCapital) {
      <div class="summary-card accent">
        <div class="card-icon">
          <mat-icon>account_balance</mat-icon>
        </div>
        <div class="card-content">
          <div class="card-value">{{ totalCostOfCapital | currency }}</div>
          <div class="card-label">Cost of Capital</div>
        </div>
      </div>

      <div class="summary-card grand-total">
        <div class="card-icon">
          <mat-icon>receipt_long</mat-icon>
        </div>
        <div class="card-content">
          <div class="card-value">{{ grandTotal | currency }}</div>
          <div class="card-label">Grand Total</div>
        </div>
      </div>
    }
  </div>

  <!-- Success rate indicator -->
  @if (results.length > 0) {
    <div class="success-rate">
      <div class="rate-label">Success Rate</div>
      <div class="rate-bar">
        <div class="rate-fill" [style.width.%]="successRate"></div>
      </div>
      <div class="rate-percentage">{{ successRate.toFixed(1) }}%</div>
    </div>
  }

  <!-- Successful payments -->
  @if (successfulPayments.length > 0) {
    <div class="results-section">
      <h4>Successful Payments ({{ successfulPayments.length }})</h4>
      <div class="table-container">
        <table mat-table [dataSource]="successfulPayments" class="results-table">
          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef>Status</th>
            <td mat-cell *matCellDef="let result">
              <span class="status-badge" [class]="getStatusClass(result.status)">
                <mat-icon>{{ getStatusIcon(result.status) }}</mat-icon>
                {{ getOutcomeLabel(result) }}
              </span>
            </td>
          </ng-container>

          <ng-container matColumnDef="accountNumber">
            <th mat-header-cell *matHeaderCellDef>Account Number</th>
            <td mat-cell *matCellDef="let result">{{ result.row.accountNumber }}</td>
          </ng-container>

          <ng-container matColumnDef="customerName">
            <th mat-header-cell *matHeaderCellDef>Customer Name</th>
            <td mat-cell *matCellDef="let result">
              {{ result.customer?.displayName || result.customer?.companyName || '-' }}
            </td>
          </ng-container>

          <ng-container matColumnDef="amount">
            <th mat-header-cell *matHeaderCellDef>Amount</th>
            <td mat-cell *matCellDef="let result" class="amount-cell">
              {{ result.row.amount | currency }}
            </td>
          </ng-container>

          @if (hasCostOfCapital) {
            <ng-container matColumnDef="costOfCapital">
              <th mat-header-cell *matHeaderCellDef>Cost of Capital</th>
              <td mat-cell *matCellDef="let result" class="amount-cell">
                {{ (result.row.costOfCapitalAmount || 0) | currency }}
              </td>
            </ng-container>

            <ng-container matColumnDef="total">
              <th mat-header-cell *matHeaderCellDef>Total</th>
              <td mat-cell *matCellDef="let result" class="amount-cell total-cell">
                <strong>{{ (result.row.amount + (result.row.costOfCapitalAmount || 0)) | currency }}</strong>
              </td>
            </ng-container>
          }

          <ng-container matColumnDef="invoiceNumber">
            <th mat-header-cell *matHeaderCellDef>Invoice #</th>
            <td mat-cell *matCellDef="let result">{{ result.row.invoiceNumber || '-' }}</td>
          </ng-container>

          <ng-container matColumnDef="transactionId">
            <th mat-header-cell *matHeaderCellDef>Transaction ID</th>
            <td mat-cell *matCellDef="let result" class="transaction-id">
              {{ result.transactionId || '-' }}
            </td>
          </ng-container>

          <ng-container matColumnDef="message">
            <th mat-header-cell *matHeaderCellDef>Result</th>
            <td mat-cell *matCellDef="let result" class="message-cell">
              {{ result.responseMessage || 'Approved' }}
            </td>
          </ng-container>

          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef>Actions</th>
            <td mat-cell *matCellDef="let result" class="actions-cell">
              @if (result.transactionId) {
                <button
                  mat-icon-button
                  color="primary"
                  (click)="onViewReceipt(result.transactionId)"
                  matTooltip="View Receipt"
                  class="action-button"
                >
                  <mat-icon>receipt</mat-icon>
                </button>
                <button
                  mat-icon-button
                  color="primary"
                  (click)="onDownloadReceipt(result.transactionId)"
                  matTooltip="Download Receipt"
                  class="action-button"
                >
                  <mat-icon>download</mat-icon>
                </button>
              }
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="getDisplayedColumns()"></tr>
          <tr mat-row *matRowDef="let row; columns: getDisplayedColumns();"></tr>
        </table>
      </div>
    </div>
  }

  <!-- Failed payments -->
  @if (failedPayments.length > 0) {
    <div class="results-section error-section">
      <h4>Failed Payments ({{ failedPayments.length }})</h4>
      <div class="table-container">
        <table mat-table [dataSource]="failedPayments" class="results-table">
          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef>Status</th>
            <td mat-cell *matCellDef="let result">
              <span class="status-badge" [class]="getStatusClass(result.status)">
                <mat-icon>{{ getStatusIcon(result.status) }}</mat-icon>
                {{ getOutcomeLabel(result) }}
              </span>
            </td>
          </ng-container>

          <ng-container matColumnDef="accountNumber">
            <th mat-header-cell *matHeaderCellDef>Account Number</th>
            <td mat-cell *matCellDef="let result">{{ result.row.accountNumber }}</td>
          </ng-container>

          <ng-container matColumnDef="customerName">
            <th mat-header-cell *matHeaderCellDef>Customer Name</th>
            <td mat-cell *matCellDef="let result">
              {{ result.customer?.displayName || result.customer?.companyName || '-' }}
            </td>
          </ng-container>

          <ng-container matColumnDef="amount">
            <th mat-header-cell *matHeaderCellDef>Amount</th>
            <td mat-cell *matCellDef="let result" class="amount-cell">
              {{ result.row.amount | currency }}
            </td>
          </ng-container>

          <ng-container matColumnDef="invoiceNumber">
            <th mat-header-cell *matHeaderCellDef>Invoice #</th>
            <td mat-cell *matCellDef="let result">{{ result.row.invoiceNumber || '-' }}</td>
          </ng-container>

          <ng-container matColumnDef="transactionId">
            <th mat-header-cell *matHeaderCellDef>Transaction ID</th>
            <td mat-cell *matCellDef="let result" class="transaction-id">
              {{ result.transactionId || '-' }}
            </td>
          </ng-container>

          <ng-container matColumnDef="message">
            <th mat-header-cell *matHeaderCellDef>Reason</th>
            <td mat-cell *matCellDef="let result" class="error-message-cell">
              {{ result.error || result.responseMessage || 'The payment was not approved.' }}
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns;" class="error-row"></tr>
        </table>
      </div>
    </div>
  }

  <!-- Skipped payments -->
  @if (skippedPayments.length > 0) {
    <div class="results-section warning-section">
      <h4>Skipped Payments ({{ skippedPayments.length }})</h4>
      <p class="section-description">These payments were not processed due to missing customers or payment methods.</p>
      <div class="skipped-list">
        @for (match of skippedPayments; track match.row.rowIndex) {
          <div class="skipped-item">
            <mat-icon>warning</mat-icon>
            <div class="skipped-content">
              <div class="skipped-header">
                <strong>{{ match.row.accountNumber }}</strong>
                <span class="skipped-amount">{{ match.row.amount | currency }}</span>
              </div>
              <div class="skipped-error">{{ match.error }}</div>
            </div>
          </div>
        }
      </div>
    </div>
  }

  @if (results.length === 0 && skippedPayments.length === 0) {
    <div class="empty-state">
      <mat-icon>inbox</mat-icon>
      <p>No results to display</p>
    </div>
  }
</div>